import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { Loader2, Info } from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
} from "recharts";

const NAVY = "#0A192F", GOLD = "#B7955B";
const PIE_COLORS = [NAVY, GOLD, "#94A3B8"];

function Kpi({ label, value, sub, onClick, testid, tone }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left bg-white border rounded-[3px] p-4 hover:shadow-md transition-shadow w-full ${
        tone === "warn" ? "border-amber-300" : "border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-serif text-slate-900 mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </button>
  );
}

function ChartBox({ title, children, note }) {
  return (
    <div className="bg-white border border-slate-200 rounded-[3px] p-4">
      <div className="text-sm font-semibold text-slate-800 mb-2">{title}</div>
      <div className="h-56">{children}</div>
      {note && <div className="text-[11px] text-slate-500 mt-2">{note}</div>}
    </div>
  );
}

export default function OverviewTab({ onOpen }) {
  const [d, setD] = useState(null);
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/portfolio-active/overview", { params: { dal: dal || undefined, al: al || undefined } });
      setD(r.data);
    } finally { setBusy(false); }
  }, [dal, al]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const k = d.kpi, g = d.grafici;

  return (
    <div className="space-y-5" data-testid="pa-overview">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Kpi label="Clienti personali" value={k.clienti_personali} sub={`${k.n_pratiche_totali} pratiche collegate`} onClick={() => onOpen("clienti")} testid="kpi-personali" />
        <Kpi label="Portafoglio Prestiti" value={k.prestiti} onClick={() => onOpen("clienti", { portale: "prestiti" })} testid="kpi-prestiti" />
        <Kpi label="Portafoglio Cessioni" value={k.cessioni} onClick={() => onOpen("clienti", { portale: "cessioni" })} testid="kpi-cessioni" />
        <Kpi label="Da classificare" value={k.da_classificare} onClick={() => onOpen("clienti", { portale: "unassigned" })} testid="kpi-classificare" tone={k.da_classificare > 0 ? "warn" : undefined} />
        <Kpi label="Con pratiche aperte" value={k.con_pratiche_aperte} sub="clienti unici" onClick={() => onOpen("clienti", { has: "aperte" })} testid="kpi-aperte" />
        <Kpi label="Con pratiche liquidate" value={k.con_pratiche_liquidate} sub={`${k.sovrapposti_aperte_e_liquidate} anche con aperte`} onClick={() => onOpen("clienti", { has: "liquidate" })} testid="kpi-liquidate" />
        <Kpi label="Recall oggi" value={k.recall_oggi} onClick={() => onOpen("clienti", { recall: "oggi" })} testid="kpi-recall-oggi" />
        <Kpi label="Recall scaduti" value={k.recall_scaduti} onClick={() => onOpen("clienti", { recall: "scaduto" })} testid="kpi-recall-scaduti" tone={k.recall_scaduti > 0 ? "warn" : undefined} />
        <Kpi label="Recall senza data" value={k.recall_senza_data} onClick={() => onOpen("clienti", { recall: "senza" })} testid="kpi-recall-senza" tone={k.recall_senza_data > 0 ? "warn" : undefined} />
        <Kpi label="Rifiutate: dati da recuperare" value={d.qualita_dati.rifiutate_senza_data_esito + d.qualita_dati.rifiutate_senza_motivo}
          sub={`${d.qualita_dati.rifiutate_senza_data_esito} senza data esito · ${d.qualita_dati.rifiutate_senza_motivo} senza motivo`}
          onClick={() => onOpen("rifiutate")} testid="kpi-qualita" tone="warn" />
      </div>
      <div className="text-[11px] text-slate-500 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {d.nota_conteggi}</div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-600">Periodo pratiche (solo grafici):</span>
        <input type="month" value={dal} onChange={(e) => setDal(e.target.value)} data-testid="pa-period-dal"
          className="border border-slate-300 rounded-[3px] px-2 py-1 text-sm" />
        <span className="text-slate-400">→</span>
        <input type="month" value={al} onChange={(e) => setAl(e.target.value)} data-testid="pa-period-al"
          className="border border-slate-300 rounded-[3px] px-2 py-1 text-sm" />
        {(dal || al) && <button onClick={() => { setDal(""); setAl(""); }} className="text-xs text-slate-500 underline">tutte le date</button>}
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        <span className="text-[11px] text-slate-400">Il filtro NON cambia i totali clienti qui sopra.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartBox title="Composizione del portafoglio (clienti unici)">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={g.composizione.filter((x) => x.value > 0)} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={2}>
                {g.composizione.filter((x) => x.value > 0).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % 3]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox title="Pratiche PP / CQS per stato" note={g.nota}>
          <ResponsiveContainer>
            <BarChart data={g.pratiche_per_stato}>
              <XAxis dataKey="stato" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={45} />
              <YAxis allowDecimals={false} width={26} tick={{ fontSize: 10 }} />
              <Tooltip /><Legend />
              <Bar dataKey="PP" fill={NAVY} /><Bar dataKey="CQS" fill={GOLD} />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox title="Liquidazioni documentate per mese"
          note={`${g.etichetta_importi}${g.liquidazioni_importi_mancanti > 0 ? ` · ${g.liquidazioni_importi_mancanti} senza importo (non valgono zero: escluse dalla somma)` : ""}`}>
          <ResponsiveContainer>
            <BarChart data={g.liquidazioni_mensili}>
              <XAxis dataKey="mese" tick={{ fontSize: 10 }} />
              <YAxis width={60} tick={{ fontSize: 10 }} tickFormatter={(v) => fmtEUR(v)} />
              <Tooltip formatter={(v, n) => n === "importo_documentato" ? [fmtEUR(v), "Importo documentato"] : [v, "N° pratiche"]} />
              <Legend formatter={(v) => v === "importo_documentato" ? "Importo documentato" : "N° pratiche"} />
              <Bar dataKey="importo_documentato" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
        <ChartBox title="Pratiche rifiutate / ritirate per mese" note="Mese di caricamento (la data effettiva del rifiuto, quando manca, è segnalata da recuperare)">
          <ResponsiveContainer>
            <BarChart data={g.rifiutate_mensili}>
              <XAxis dataKey="mese" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} width={26} tick={{ fontSize: 10 }} />
              <Tooltip /><Legend />
              <Bar dataKey="rifiutate" stackId="a" fill="#be123c" name="Rifiutate" />
              <Bar dataKey="ritirate" stackId="a" fill="#94a3b8" name="Ritirate" />
            </BarChart>
          </ResponsiveContainer>
        </ChartBox>
      </div>
    </div>
  );
}
